import { useState, type MutableRefObject } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { fsDelete, fsMkdir, fsRename, fsUpload, type FsItem } from '../transport/api'

/** One file in a drop whose name is already taken in the folder on screen. */
export type Clash = {
  /** The name the incoming file carries. */
  name: string
  /** The entry already in the folder. Spelled as the FOLDER spells it, which on a
   *  case-insensitive machine is not always how the new file spells it. */
  existing: string
  /** A folder cannot be replaced by a file — the edge refuses, so we never offer it. */
  isDir: boolean
}

/**
 * Which incoming files land on a name that is already there.
 *
 * Compared CASE-INSENSITIVELY, because half this fleet is macOS, where `Report.pdf`
 * and `report.pdf` are one file — and the cost of being wrong is lopsided. A false
 * match on Linux costs a question the person answers with "keep both", which is
 * exactly what would have happened anyway; a MISS on macOS means never asking, and
 * the person gets the "A (1)" they wrote in to complain about.
 */
function clashesIn(entries: FsItem[], files: File[]): Clash[] {
  const byLower = new Map(entries.map((e) => [e.name.toLowerCase(), e]))
  const out: Clash[] = []
  for (const f of files) {
    const hit = byLower.get(f.name.toLowerCase())
    if (hit) out.push({ name: f.name, existing: hit.name, isDir: hit.type === 'dir' })
  }
  return out
}

/**
 * The browser's MUTATIONS — mkdir, upload, delete, rename — and the UI state that
 * belongs to them (the mkdir field, multi-select, upload progress, the parked drop
 * waiting on a collision answer). Split out of FileBrowser.tsx in M6 (拆面板,
 * 2026-09-03), moved verbatim; rename and the collision question added 2026-09-14.
 *
 * `cwdRef`, not a cwd value: mutations must target the directory actually on screen,
 * including when it came from the server's default rather than from `path`, and a ref
 * is how the original avoided a stale-closure cwd in a mutation created renders ago.
 * `entriesRef` is there for the same reason — the collision check has to be against
 * the listing as it is when the files are dropped, not as it was when the callback
 * was built.
 */
export function useFsActions(
  cwdRef: MutableRefObject<string>,
  host: string | null | undefined,
  entriesRef: MutableRefObject<FsItem[]>,
) {
  const qc = useQueryClient()
  const [mkdirOpen, setMkdirOpen] = useState(false)
  const [newName, setNewName] = useState('')
  const [selectMode, setSelectMode] = useState(false)
  const [picked, setPicked] = useState<Set<string>>(new Set())

  const refresh = () => void qc.invalidateQueries({ queryKey: ['fs'] })

  const mkdir = useMutation({
    mutationFn: () => fsMkdir(cwdRef.current, newName.trim(), host),
    onSuccess: () => {
      setNewName('')
      setMkdirOpen(false)
      refresh()
    },
  })

  /*
   * Progress is a NUMBER, not just a spinner: this lane carries up to 5 GB in 512 KiB
   * slices, and "uploading…" for twenty minutes is indistinguishable from a hang —
   * which this repo has learned is how people describe anything silent.
   */
  const [uploadPct, setUploadPct] = useState<number | null>(null)
  const upload = useMutation({
    mutationFn: ({
      files,
      overwrite,
    }: {
      files: File[]
      // `| undefined` explicitly: exactOptionalPropertyTypes is on, so "may be absent"
      // and "may be undefined" are different types here and the caller passes the key.
      overwrite?: ReadonlySet<string> | undefined
    }) =>
      fsUpload(cwdRef.current, files, host, (pct) => setUploadPct(pct), overwrite),
    onSuccess: refresh,
    onSettled: () => setUploadPct(null),
  })

  /*
   * A drop held back until the person answers what to do about the names it would
   * take — owner, 2026-09-14, relaying a user: 我上传了一个文件A … 我其实是想覆盖
   * 而不是生成一个 A_1.
   *
   * The question is asked BEFORE any byte moves, which is the only moment it can be
   * asked honestly: the edge resolves the name at finish, so by the time an upload
   * could report a collision the second copy already exists, and "would you like to
   * replace it" would really be "shall I clean up after myself".
   *
   * The check reads the listing on screen, so it can be WRONG in one direction — a
   * file that appeared since the last refresh is not in it, no question is asked, and
   * the edge keeps both exactly as it always did. That is the safe direction to be
   * wrong in, and it is the behaviour this feature is changing, not a new hazard.
   */
  const [pending, setPending] = useState<{ files: File[]; clashes: Clash[] } | null>(null)

  const startUpload = (files: File[]) => {
    if (!files.length) return
    const clashes = clashesIn(entriesRef.current, files)
    if (clashes.length === 0) {
      upload.mutate({ files })
      return
    }
    setPending({ files, clashes })
  }

  /**
   * Answer the parked drop. Three answers, not a boolean: 'cancel' uploads NOTHING,
   * and folding it into "not replace" would have made the ✕ mean "keep both" — a
   * dismissal that quietly does the thing it was dismissing.
   */
  const resolveUpload = (choice: 'cancel' | 'keep' | 'replace') => {
    const p = pending
    setPending(null)
    if (!p || choice === 'cancel') return
    // Folders are never in the set: a file cannot replace a folder, the edge refuses
    // it, and offering it would be a button whose only outcome is an error.
    const overwrite =
      choice === 'replace'
        ? new Set(p.clashes.filter((c) => !c.isDir).map((c) => c.name))
        : undefined
    upload.mutate({ files: p.files, overwrite })
  }

  const remove = useMutation({
    mutationFn: async (targets: string[]) => {
      for (const t of targets) await fsDelete(t, host)
    },
    onSuccess: () => {
      setPicked(new Set())
      setSelectMode(false)
      refresh()
    },
  })

  const rename = useMutation({
    mutationFn: ({ path, to }: { path: string; to: string }) => fsRename(path, to.trim(), host),
    onSuccess: refresh,
  })

  return {
    mkdir, upload, remove, rename, refresh, uploadPct,
    mkdirOpen, setMkdirOpen, newName, setNewName,
    selectMode, setSelectMode, picked, setPicked,
    startUpload, pending, resolveUpload,
  }
}
